import { useState, useEffect } from "react";
import { Credentials, getFileDownloadUrl } from "../utils/fileUtils";

/**
 * Hook to fetch only file content (binary data) from CDF
 * Should be called after metadata has been fetched and update check has been performed
 */
export const useFetchFileContent = (
  appId: string | undefined,
  credentials: Credentials | null,
  shouldFetch: boolean, // Only fetch when this is true
  getFileDownloadUrlFunction: (
    externalId: string,
    credentials: Credentials
  ) => Promise<{ downloadUrl: string }> = getFileDownloadUrl
): {
  binaryData: ArrayBuffer | undefined;
  isLoading: boolean;
  error: Error | null;
} => {
  const [binaryData, setBinaryData] = useState<ArrayBuffer | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    const fetchContent = async () => {
      if (!appId || !credentials || !shouldFetch) {
        // Don't fetch if conditions aren't met
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get download URL
        const { downloadUrl } = await getFileDownloadUrlFunction(
          appId,
          credentials
        );

        // Fetch the file content
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch file content: ${response.statusText}`
          );
        }
        const data = await response.arrayBuffer();

        if (isActive) {
          setBinaryData(data);
        }
      } catch (err) {
        if (isActive) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      isActive = false;
    };
  }, [appId, credentials, shouldFetch, getFileDownloadUrlFunction]);

  return {
    binaryData,
    isLoading,
    error,
  };
};
